import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { apiService } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import {
  sanitarywareService,
  buyerService,
} from "../../services/enhancedCrudService";
import Select from "react-select";
import Pagination from "../../components/Pagination";

const BuyerInquiryList = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInquiry, setEditInquiry] = useState(null);
  const [editFormData, setEditFormData] = useState({
    status: "",
    notes: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    grade: "",
    limit: 10,
    ordering: "",
    page: 1,
    product_type: "",
    unit: "",
    status: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrevious: false,
  });

  const PRODUCT_TYPE = [
    { value: "tiles", label: "Tiles" },
    { value: "sanitaryware", label: "Sanitaryware" },
  ];

  const GRADE_CHOICES = [
    { value: "premium", label: "Premium" },
    { value: "otp", label: "One Time Premium" },
    { value: "std_ii", label: "STD-II" },
    { value: "com_iii", label: "Com-III" },
    { value: "eco_iv", label: "Reject/DD (Eco-IV)" },
    { value: "mix", label: "Mix" },
  ];

  const UNIT_CHOICES = [
    { value: "box", label: "Box" },
    { value: "sqm", label: "Square Meter" },
    { value: "sqft", label: "Square Foot" },
    { value: "pieces", label: "Pieces" },
    { value: "ton", label: "Ton" },
    { value: "set", label: "Set" },
  ];

  const STATUS_CHOICES = [
    { value: "not_started", label: "Not Started" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
  ];

  const fetchTypes = async () => {
    try {
      const response = await sanitarywareService.getTypes();
      let formattedTypes = response.types.map((type) => {
        return {
          value: type.id,
          label: type.name,
        };
      });
      setTypes(formattedTypes);
    } catch (error) {
      console.error("Error fetching types:", error);
    }
  };

  const fetchInquiries = async (params = searchParams) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (
          params[key] !== "" &&
          params[key] !== null &&
          params[key] !== undefined
        ) {
          queryParams.append(key, params[key]);
        }
      });
      const endpoint = `${
        API_ENDPOINTS.BUYERS.INQUIRY_LIST
      }?${queryParams.toString()}`;
      const response = await apiService.get(endpoint);
      if (response.count !== undefined) {
        const totalPages = Math.ceil(response.count / params.limit);
        setPagination({
          currentPage: params.page,
          totalPages: totalPages,
          totalCount: response.count,
          hasNext: !!response.next,
          hasPrevious: !!response.previous,
        });
      }

      if (response.results) {
        setInquiries(response.results || []);
      } else {
        toast.error(response.message || "Failed to fetch inquiries");
      }
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      toast.error("Failed to fetch inquiries");
    } finally {
      setLoading(false);
    }
  };


  const handlePageChange = (newPage) => {
    const newParams = { ...searchParams, page: newPage };
    setSearchParams(newParams);
    fetchInquiries(newParams);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };


  const getStatusDisplayText = (status) => {
    const statusMap = {
      not_started: "Not Started",
      in_progress: "In Progress",
      completed: "Completed",
    };
    return statusMap[status] || status || "N/A";
  };

  const handleViewDetails = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInquiry(null);
  };

  const openEditModal = (inquiry) => {
    setEditInquiry(inquiry);
    setEditFormData({
      status: inquiry.status || "",
      notes: inquiry.notes || "",
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditInquiry(null);
    setEditFormData({
      status: "",
      notes: "",
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateInquiry = async () => {
    if (!editInquiry?.inquiry_id) {
      toast.error("Invalid inquiry data");
      return;
    }

    setEditLoading(true);
    try {
      const response = await buyerService.updateInquiry(
        editInquiry.inquiry_id,
        editFormData
      );
      if (response && response.inquiry_id) {
        toast.success("Inquiry updated successfully");
        handleCloseEditModal();
        fetchInquiries();
      } else {
        toast.error(response.message || "Failed to update inquiry");
      }
    } catch (error) {
      console.error("Error updating inquiry:", error);
      toast.error("Failed to update inquiry");
    } finally {
      setEditLoading(false);
    }
  };

  const downloadPDF = async (inquiryId) => {
    try {
      const endpoint = API_ENDPOINTS.BUYERS.INQUIRY_PDF(inquiryId);
      const response = await apiService.get(endpoint, {
        responseType: "blob",
      });
      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `inquiry_${inquiryId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  useEffect(() => {
    fetchInquiries();
    fetchTypes();
  }, []);

  return (
    <>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Buyer Inquiry List</h3>
          </div>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Search Filters</h5>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex col-md-12">
                    <div className="col-md-2 me-2">
                      <div className="form-group">
                        <label htmlFor="grade">Grade</label>
                        <Select
                          options={GRADE_CHOICES}
                          value={
                            GRADE_CHOICES.find(
                              (option) => option.value === searchParams.grade
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            const newParams = {
                              ...searchParams,
                              grade: selectedOption ? selectedOption.value : "",
                              page: 1
                            };
                            setSearchParams(newParams);
                            fetchInquiries(newParams);
                          }}
                          isClearable
                          placeholder="Select grade"
                        />
                      </div>
                    </div>
                    <div className="col-md-2 me-2">
                      <div className="form-group">
                        <label htmlFor="product_type">Product Type</label>
                        <Select
                          options={PRODUCT_TYPE}
                          value={
                            PRODUCT_TYPE.find(
                              (option) => option.value === searchParams.product_type
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            const newParams = {
                              ...searchParams,
                              product_type: selectedOption ? selectedOption.value : "",
                              page: 1
                            };
                            setSearchParams(newParams);
                            fetchInquiries(newParams);
                          }}
                          isClearable
                          placeholder="Select product type"
                        />
                      </div>
                    </div>
                    <div className="col-md-2 me-2">
                      <div className="form-group">
                        <label htmlFor="unit">Unit</label>
                        <Select
                          options={UNIT_CHOICES}
                          value={
                            UNIT_CHOICES.find(
                              (option) => option.value === searchParams.unit
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            const newParams = {
                              ...searchParams,
                              unit: selectedOption ? selectedOption.value : "",
                              page: 1
                            };
                            setSearchParams(newParams);
                            fetchInquiries(newParams);
                          }}
                          isClearable
                          placeholder="Select unit"
                        />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="form-group">
                        <label htmlFor="status">Status</label>
                        <Select
                          options={STATUS_CHOICES}
                          value={
                            STATUS_CHOICES.find(
                              (option) => option.value === searchParams.status
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            const newParams = {
                              ...searchParams,
                              status: selectedOption ? selectedOption.value : "",
                              page: 1
                            };
                            setSearchParams(newParams);
                            fetchInquiries(newParams);
                          }}
                          isClearable
                          placeholder="Select status"
                        />
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Inquiries</h5>
              <div className="card-tools">
                <span className="badge bg-primary">
                  Total: {pagination.totalCount || inquiries.length}
                </span>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Sr.No</th>
                          <th>Buyer</th>
                          <th>Mobile</th>
                          <th>Product Type</th>
                          <th>Grade</th>
                          <th>Unit</th>
                          <th>Quantity</th>
                          <th>Nots</th>
                          <th>Created Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inquiries.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center">
                              No inquiries found
                            </td>
                          </tr>
                        ) : (
                          inquiries.map((inquiry, index) => (
                            <tr key={index}>
                              <td>
                                {index + 1 + (pagination.currentPage - 1) * 10}
                              </td>
                              <td>{inquiry.firm_name}</td>
                              <td>{inquiry.mobile}</td>
                              <td>{inquiry.product_type || "N/A"}</td>
                              <td>{inquiry.grade || "N/A"}</td>
                              <td>{inquiry.unit || "N/A"}</td>
                              <td>{inquiry.quantity || "N/A"}</td>
                              <td>{inquiry.notes || "N/A"}</td>
                              <td>{formatDate(inquiry.created_at)}</td>
                              <td>
                                {getStatusDisplayText(inquiry.status)}
                              </td>
                              <td>
                                <div className="actions">
                                  <button
                                    className="btn btn-sm btn-outline-primary me-2"
                                    onClick={() => openEditModal(inquiry)}
                                    title="Edit"
                                  >
                                    <i className="fe fe-edit"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-success me-2"
                                    title="View Details"
                                    onClick={() => handleViewDetails(inquiry)}
                                  >
                                    <i className="fe fe-eye"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-info me-2"
                                    title="Download PDF"
                                    onClick={() =>
                                      downloadPDF(inquiry.inquiry_id)
                                    }
                                  >
                                    <i className="fe fe-download"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    loading={loading}
                    showPageInfo={true}
                    className="mt-3"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {showModal && selectedInquiry && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Inquiry Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Buyer Information</h6>
                    <table className="table table-borderless">
                      <tbody>
                        <tr>
                          <td>
                            <strong>Inquiry ID:</strong>
                          </td>
                          <td>{selectedInquiry.inquiry_id || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Buyer Name:</strong>
                          </td>
                          <td>{selectedInquiry.firm_name || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Mobile:</strong>
                          </td>
                          <td>{selectedInquiry.mobile || "N/A"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">Product Information</h6>
                    <table className="table table-borderless">
                      <tbody>
                        <tr>
                          <td>
                            <strong>Product Type:</strong>
                          </td>
                          <td>{selectedInquiry.product_type || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Category:</strong>
                          </td>
                          <td>{selectedInquiry.category || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Grade:</strong>
                          </td>
                          <td>{selectedInquiry.grade || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Unit:</strong>
                          </td>
                          <td>{selectedInquiry.unit || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Quantity:</strong>
                          </td>
                          <td>{selectedInquiry.quantity || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Size:</strong>
                          </td>
                          <td>{selectedInquiry.size || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Company Material:</strong>
                          </td>
                          <td>{selectedInquiry.company_material || "N/A"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="row mt-4">
                  <div className="col-12">
                    <h6 className="text-primary mb-3">
                      Additional Information
                    </h6>
                    <table className="table table-borderless">
                      <tbody>
                        <tr>
                          <td>
                            <strong>Remarks:</strong>
                          </td>
                          <td>{selectedInquiry.remarks || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Notes:</strong>
                          </td>
                          <td>{selectedInquiry.notes || "N/A"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Status:</strong>
                          </td>
                          <td>
                            {getStatusDisplayText(selectedInquiry.status)}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Created Date:</strong>
                          </td>
                          <td>{formatDate(selectedInquiry.created_at)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {selectedInquiry.product_image && (
                  <div className="row mt-4">
                    <div className="col-12">
                      <h6 className="text-primary mb-3">Product Image</h6>
                      <div className="text-center">
                        <img
                          src={selectedInquiry.product_image}
                          alt="Product"
                          className="img-fluid rounded"
                          style={{ maxHeight: "300px", maxWidth: "100%" }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showModal && <div className="modal-backdrop fade show"></div>}

      {/* Edit Modal */}
      {showEditModal && editInquiry && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Inquiry</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <label htmlFor="status" className="form-label">
                        Status
                      </label>
                      <Select
                        options={STATUS_CHOICES}
                        value={
                          STATUS_CHOICES.find(
                            (option) => option.value === editFormData.status
                          ) || null
                        }
                        onChange={(selectedOption) => {
                          handleEditFormChange(
                            "status",
                            selectedOption ? selectedOption.value : ""
                          );
                        }}
                        isClearable
                        placeholder="Select status"
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label htmlFor="notes" className="form-label">
                        Notes
                      </label>
                      <textarea
                        className="form-control"
                        id="notes"
                        rows="4"
                        value={editFormData.notes}
                        onChange={(e) =>
                          handleEditFormChange("notes", e.target.value)
                        }
                        placeholder="Enter notes..."
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={handleCloseEditModal}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateInquiry}
                  disabled={editLoading}
                >
                  {editLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Updating...
                    </>
                  ) : (
                    "Update Inquiry"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEditModal && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default BuyerInquiryList;
